import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faForwardStep } from '@fortawesome/sharp-solid-svg-icons/faForwardStep';
import { faBackwardStep } from '@fortawesome/sharp-solid-svg-icons/faBackwardStep';
import { faPlay } from '@fortawesome/sharp-solid-svg-icons/faPlay';
import { faPlayFlip } from '@fortawesome/sharp-solid-svg-icons/faPlayFlip';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';

import style from './paging-control.module.less';
import { emptyArray } from 'ui-components/default-props/empty';
import { getDisplayedGroupItemIndexes } from '../../services/groupby/groupby-paging';
import { NumberFormattingService } from '../../services/number-formatting-service';
import { allowablePageSizes as allowablePageSizesDefaultValues } from '../../services/pagination-service';

const FIRST_PAGE = 1;

export const PagingControl = ({
  totalRecords = 0,
  viewStartingCount,
  viewEndingCount,
  pageSize,
  page,
  totalPages,
  onPageChange,
  allowablePageSizes = allowablePageSizesDefaultValues,
  numberSelected = 0,
  groupByDetailsAndCounts = emptyArray,
  containerClassName = '',
}) => {
  const [statePage, setStatePage] = useState(page);

  useEffect(() => {
    setStatePage(page);
  }, [page]);

  const notifyPageChange = useCallback(
    (newPage = statePage) => {
      onPageChange(newPage, pageSize);
    },
    [pageSize, onPageChange, statePage],
  );

  const changePage = useCallback(
    (newPage) => {
      let sanitizedPage = newPage;
      if (Number.isNaN(newPage)) {
        sanitizedPage = page;
      }
      if (sanitizedPage < 1) {
        sanitizedPage = 1;
      }
      if (sanitizedPage > totalPages) {
        sanitizedPage = totalPages;
      }
      setStatePage(sanitizedPage);
      if (sanitizedPage !== page) {
        notifyPageChange(sanitizedPage);
      }
    },
    [page, totalPages, setStatePage, notifyPageChange],
  );

  const onPageInputChange = useCallback(
    (event) => {
      setStatePage(event.target.value);
    },
    [setStatePage],
  );

  const onPageInputBlur = useCallback(
    (event) => {
      changePage(Number.parseInt(event.target.value, 10));
    },
    [changePage],
  );

  const onPageInputKeyPress = useCallback(
    (keyEvent) => {
      if (keyEvent.key !== 'Enter') {
        return;
      }
      changePage(Number.parseInt(keyEvent.target.value, 10));
    },
    [changePage],
  );

  const onPageInputClick = (event) => {
    event.target.select();
  };

  const displayedGroupbys = useMemo(() => {
    const { startDisplayGroup, endDisplayGroup } = getDisplayedGroupItemIndexes(page, pageSize, groupByDetailsAndCounts);
    if (startDisplayGroup === endDisplayGroup) {
      return endDisplayGroup;
    }
    return `${startDisplayGroup} - ${endDisplayGroup}`;
  }, [groupByDetailsAndCounts, page, pageSize]);

  const goToFirstPage = useCallback(() => {
    changePage(1);
  }, [changePage]);

  const goToLastPage = useCallback(() => {
    changePage(totalPages);
  }, [totalPages, changePage]);

  const goToNextPage = useCallback(() => {
    changePage(page + 1);
  }, [page, changePage]);

  const goToPrevPage = useCallback(() => {
    changePage(page - 1);
  }, [changePage, page]);

  const renderPagingButtons = useCallback(() => {
    const onFirstPage = page === 1;
    const onLastPage = page === totalPages;
    if (page === 1 && totalPages === 1) {
      return null;
    }
    return (
      <div className={style['paging-buttons']}>
        <div className={style['paging-back']}>
          <Button className={style['button-pager']} disabled={onFirstPage} onClick={goToFirstPage} theme='unstyled'>
            <FontAwesomeIcon icon={faBackwardStep} />
          </Button>
          <Button className={style['button-pager']} disabled={onFirstPage} onClick={goToPrevPage} theme='unstyled'>
            <FontAwesomeIcon icon={faPlayFlip} />
          </Button>
        </div>
        <div className={style['paging-current-total']}>
          <FormattedMessage
            defaultMessage='Page {input} of {totalPages}'
            description='Paging control input and display'
            id='6v2s4A'
            values={{
              input: (
                <input
                  className={style['page-jump-input']}
                  onBlur={onPageInputBlur}
                  onChange={onPageInputChange}
                  onClick={onPageInputClick}
                  onKeyPress={onPageInputKeyPress}
                  type='number'
                  value={statePage}
                />
              ),
              totalPages: NumberFormattingService.addCommasToNumber(totalPages),
            }}
          />
        </div>
        <div className={style['paging-forward']}>
          <Button className={style['button-pager']} disabled={onLastPage} onClick={goToNextPage} theme='unstyled'>
            <FontAwesomeIcon icon={faPlay} />
          </Button>
          <Button className={style['button-pager']} disabled={onLastPage} onClick={goToLastPage} theme='unstyled'>
            <FontAwesomeIcon icon={faForwardStep} />
          </Button>
        </div>
      </div>
    );
  }, [
    page,
    totalPages,
    goToFirstPage,
    goToPrevPage,
    onPageInputBlur,
    onPageInputChange,
    onPageInputKeyPress,
    statePage,
    goToNextPage,
    goToLastPage,
  ]);

  const renderNumberSelected = useCallback(() => {
    if (numberSelected < 1) {
      return null;
    }
    return (
      <div className={style['select-all-container']}>
        <div className={style['number-selected']}>
          <FormattedMessage
            defaultMessage='{numberSelected} Selected'
            description='paging.selected'
            id='5UPfGN'
            values={{
              numberSelected: NumberFormattingService.addCommasToNumber(numberSelected),
            }}
          />
        </div>
      </div>
    );
  }, [numberSelected]);

  return (
    <div className={classnames(style['paging-container'], containerClassName)}>
      <div className={style['count-container']}>
        <div>
          <FormattedMessage
            defaultMessage='Viewing {startCount} - {endCount} of {total}'
            description='paging.viewingCounts'
            id='b4dgkw'
            values={{
              startCount: NumberFormattingService.addCommasToNumber(viewStartingCount),
              endCount: NumberFormattingService.addCommasToNumber(viewEndingCount),
              total: NumberFormattingService.addCommasToNumber(totalRecords),
            }}
          />
        </div>
        {groupByDetailsAndCounts.length > 0 && (
          <div className={style['select-all-container']}>
            <div className={style['number-selected']}>
              <FormattedMessage
                defaultMessage='{groupsDisplayed} of {totalGroups} Groups'
                description='Paging Data Active Group Bys Display Information'
                id='spPNr3'
                values={{
                  groupsDisplayed: displayedGroupbys,
                  totalGroups: NumberFormattingService.addCommasToNumber(groupByDetailsAndCounts.length),
                }}
              />
            </div>
          </div>
        )}
        {renderNumberSelected()}
      </div>
      <div className={style['paging-controls']}>
        {renderPagingButtons()}
        <div className={style['paging-record-count']}>
          <DropdownMenu disabled={allowablePageSizes.length === 1} dropdownMenuShouldHaveWidthOfButton menuPosition='top-left'>
            <DropdownMenuToggler className={style['paging-record-count-input']}>{pageSize}</DropdownMenuToggler>
            {allowablePageSizes.map((allowablePageSize) => (
              <DropdownMenuItem
                key={allowablePageSize}
                onClick={() => onPageChange(FIRST_PAGE, allowablePageSize)}
                selected={allowablePageSize === pageSize}
              >
                {allowablePageSize}
              </DropdownMenuItem>
            ))}
          </DropdownMenu>
          <span className={style['per-page-wrap']}>
            <FormattedMessage defaultMessage='per page' description='paging.perPage' id='fXq6GA' />
          </span>
        </div>
      </div>
    </div>
  );
};

PagingControl.propTypes = {
  page: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  pageSize: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  totalPages: PropTypes.number.isRequired,
  totalRecords: PropTypes.number,
  viewStartingCount: PropTypes.number.isRequired,
  viewEndingCount: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  numberSelected: PropTypes.number,
  allowablePageSizes: PropTypes.arrayOf(PropTypes.number),
  groupByDetailsAndCounts: PropTypes.arrayOf(
    PropTypes.shape({
      groupByDetails: PropTypes.arrayOf(
        PropTypes.shape({
          groupByColumn: PropTypes.string,
          groupByValue: PropTypes.string,
        }),
      ),
      count: PropTypes.number,
    }),
  ),
  containerClassName: PropTypes.string,
};
